<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, resolveComponent, toRef, useId, watch } from 'vue'
import { useDialogFocus } from '~/composables/useDialogFocus'
import MmIcon from './MmIcon.vue'
import type { MmMenuItem } from './menu'
import { useMmPortalTheme } from './portalTheme'
import { useMmShellInert } from './shellInert'
import { useMmText } from './text'
const props = defineProps<{ modelValue: boolean; label: string; items: MmMenuItem[]; variant?: 'mega'; mobileBreakpoint?: number; mobileTitle?: string; title?: string }>()
const { t } = useMmText()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; select: [item: Extract<MmMenuItem, { id: string }>] }>()
const origin = ref<HTMLElement | null>(null), anchor = ref<HTMLElement | null>(null)
const position = ref({ '--menu-left': '8px', '--menu-top': '8px' })
const id = `mm-menu-${useId()}`
const open = toRef(props, 'modelValue'), mobile = ref(false)
const menuCount = useState('mm-open-menus', () => 0)
// Synchronize the breakpoint before opening, including externally opened menus.
// Otherwise stale mobile state briefly locks a desktop menu as a modal; its
// deferred focus restoration then steals focus back from the first menu item.
watch(open, visible => { if (visible && import.meta.client) mobile.value = window.innerWidth < (props.mobileBreakpoint ?? 768) }, { flush: 'sync' })
const modal = computed(() => open.value && mobile.value)
const close = () => emit('update:modelValue', false)
const { dialog, closeButton, handleKeydown } = useDialogFocus(modal, close)
useMmShellInert(modal)
const { theme, hue } = useMmPortalTheme(origin, open)
let registered = false
let returnTarget: HTMLElement | null = null
let sizeObserver: ResizeObserver | undefined
const buttons = () => Array.from(dialog.value?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled):not([aria-disabled="true"]), [role="menuitemradio"]:not(:disabled):not([aria-disabled="true"])') || [])
const place = () => {
  mobile.value = window.innerWidth < (props.mobileBreakpoint ?? 768)
  const rect = (anchor.value || origin.value)?.getBoundingClientRect()
  if (!rect || !dialog.value || mobile.value) return
  const width = dialog.value.offsetWidth, height = dialog.value.offsetHeight
  const intended = props.variant === 'mega' ? rect.left + rect.width / 2 - width / 2 : rect.right - width
  const left = Math.max(8, Math.min(intended, window.innerWidth - width - 8))
  const gap = props.variant === 'mega' ? 10 : 6
  const preferredTop = rect.bottom + gap + height <= window.innerHeight - 8 ? rect.bottom + gap : rect.top - height - gap
  const top = Math.max(8, Math.min(preferredTop, window.innerHeight - height - 8))
  position.value = { '--menu-left': `${left}px`, '--menu-top': `${top}px` }
}
const toggle = (event?: Event) => {
  if (event?.currentTarget instanceof HTMLElement) anchor.value = event.currentTarget
  emit('update:modelValue', !props.modelValue)
}
const keyboard = (event: KeyboardEvent) => {
  if (mobile.value) handleKeydown(event)
  else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close() }
  const items = buttons()
  if (!items.length) return
  if (event.key === 'Tab' && !mobile.value) {
    const at = items.indexOf(document.activeElement as HTMLElement)
    if (event.shiftKey && at <= 0 || !event.shiftKey && at === items.length - 1) { event.preventDefault(); items[event.shiftKey ? items.length - 1 : 0]?.focus() }
  }
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return
  event.preventDefault()
  const index = items.indexOf(document.activeElement as HTMLElement)
  const target = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length
  items[target]?.focus()
}
const outside = (event: PointerEvent) => {
  if (open.value && !dialog.value?.contains(event.target as Node) && !origin.value?.contains(event.target as Node)) close()
}
const escape = (event: KeyboardEvent) => {
  if (open.value && event.key === 'Escape' && !event.defaultPrevented) {
    event.preventDefault()
    close()
  }
}
const select = async (item: Extract<MmMenuItem, { id: string }>, event?: Event) => {
  if (item.disabled) { event?.preventDefault(); return }
  if (!item.keepOpen) {
    close()
    // Release the sheet's focus/inert locks before a selected action opens a
    // dialog. Its focus history must contain the persistent trigger.
    await nextTick()
    await nextTick()
    returnTarget?.focus({ preventScroll: true })
  }
  emit('select', item)
}
watch(open, async visible => {
  if (!import.meta.client) return
  window.removeEventListener('resize', place)
  window.removeEventListener('scroll', place, true)
  sizeObserver?.disconnect()
  if (registered) { menuCount.value--; registered = false }
  if (!visible) { await nextTick(() => returnTarget?.focus({ preventScroll: true })); return }
  mobile.value = window.innerWidth < (props.mobileBreakpoint ?? 768)
  returnTarget = anchor.value || document.activeElement as HTMLElement
  menuCount.value++; registered = true
  await nextTick()
  if (!props.modelValue) return
  place()
  ;(buttons()[0] || dialog.value)?.focus({ preventScroll: true })
  sizeObserver = new ResizeObserver(place)
  if (dialog.value) sizeObserver.observe(dialog.value)
  window.addEventListener('resize', place)
  window.addEventListener('scroll', place, true)
})
watch(() => props.items, async () => { if (open.value) { await nextTick(); place(); (buttons()[0] || dialog.value)?.focus({ preventScroll: true }) } })
watch(mobile, async () => {
  if (!open.value) return
  await nextTick()
  if (open.value) { place(); (buttons()[0] || dialog.value)?.focus({ preventScroll: true }) }
})
onMounted(() => {
  mobile.value = window.innerWidth < (props.mobileBreakpoint ?? 768)
  document.addEventListener('pointerdown', outside, true)
  document.addEventListener('keydown', escape)
})
onBeforeUnmount(() => {
  if (!import.meta.client) return
  if (registered) menuCount.value--
  sizeObserver?.disconnect()
  window.removeEventListener('resize', place)
  window.removeEventListener('scroll', place, true)
  document.removeEventListener('pointerdown', outside, true)
  document.removeEventListener('keydown', escape)
})
</script>

<template>
  <span ref="origin" class="mm-menu-anchor"><slot name="trigger" :toggle="toggle" :expanded="modelValue" :id="id" /></span>
  <Teleport to="#teleports">
    <div v-if="modelValue" class="mm-root mm-overlay-root mm-menu-layer" :data-theme="theme" :style="{ '--live-h': hue }">
      <div :class="mobile ? 'scrim mm-menu-scrim' : 'mm-menu-dismiss'" aria-hidden="true" @click.self="close" />
      <div :id="id" ref="dialog" class="mm-menu" :class="[mobile ? 'sheet menu-sheet' : variant === 'mega' ? 'mega' : 'menu', { 'mega-sheet': mobile && variant === 'mega' }]" :style="position" role="menu" :aria-label="label" tabindex="-1" @keydown="keyboard">
        <div v-if="mobile" class="mm-menu-top"><span class="t-small faint">{{ mobileTitle || title || label }}</span><button ref="closeButton" type="button" class="mm-menu-close" :aria-label="`${t('common.close')} ${label}`" @click="close"><MmIcon name="x" :size="20" /></button></div>
        <div v-else-if="title" class="menu-head">{{ title }}</div>
        <slot name="header" />
        <template v-for="(item, i) in items" :key="item.id || i">
          <div v-if="item.head" class="menu-head" role="presentation">{{ item.head }}</div>
          <hr v-else-if="item.divider" role="separator">
          <component :is="item.to ? resolveComponent('NuxtLink') : item.href ? 'a' : 'button'" v-else :to="item.to" :href="item.href" :type="!item.to && !item.href ? 'button' : undefined" :role="item.checked !== undefined ? 'menuitemradio' : 'menuitem'" :aria-checked="item.checked" :aria-disabled="item.disabled || undefined" :class="{ danger: item.danger }" :disabled="item.disabled" :style="item.color ? { '--tool-color': item.color } : undefined" @click="select(item, $event)">
            <template v-if="variant === 'mega'"><span class="tile-ic"><MmIcon :name="item.icon || 'right'" :size="20" /></span><span><strong>{{ item.label }}</strong><small v-if="item.meta">{{ item.meta }}</small></span></template>
            <template v-else><MmIcon v-if="item.icon" :name="item.icon" :size="18" /><span>{{ item.label }}</span><span v-if="item.meta" class="menu-meta">{{ item.meta }}</span><MmIcon v-if="item.checked" name="check" :size="18" class="menu-meta" /></template>
          </component>
        </template>
        <slot name="footer" />
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* Nuxt provides #teleports outside the inert shell. A menu opened from the
   Now Playing sheet must also paint above that sheet, not behind it. */
.mm-root.mm-overlay-root.mm-menu-layer { display: block; position: fixed; inset: 0; z-index: calc(var(--z-sheet) + 1); pointer-events: none; }
.mm-menu-layer > * { pointer-events: auto; }
.mm-menu-dismiss { position: fixed; inset: 0; z-index: calc(var(--z-menu) - 1); }
.mm-menu-top { display: flex; align-items: center; justify-content: space-between; gap: var(--s-3); min-width: 0; grid-column: 1 / -1; }
.mm-menu-top > span { min-width: 0; overflow-wrap: anywhere; }
.mm-menu-top .mm-menu-close { display: grid; place-items: center; width: 44px; min-width: 44px; min-height: 44px; padding: 0; flex: none; }
.mm-menu.mega-sheet { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--s-1); }
.mega-sheet::before, .mega-sheet > .menu-head, .mega-sheet > hr { grid-column: 1 / -1; }
.mm-menu.mega-sheet > a { display: grid; grid-template-columns: 40px minmax(0, 1fr); align-items: center; gap: var(--s-3); min-width: 0; min-height: 64px; padding: var(--s-3); border-radius: var(--r-md); }
.mega-sheet > a:last-child { grid-column: 1 / -1; }
.mega-sheet > a:hover { background: var(--surface-2); }
.mega-sheet > a > span:last-child { min-width: 0; overflow-wrap: anywhere; }
.mega-sheet .tile-ic { display: grid; place-items: center; width: 40px; height: 40px; border-radius: var(--r-cover); background: var(--tool-color, var(--accent)); color: var(--accent-ink); }
.mega-sheet .tile-ic :deep(.ic) { color: inherit; }
.mega-sheet strong { display: block; font: 600 var(--t-small)/1.35 var(--font-ui); }
.mega-sheet small { display: block; margin-top: var(--s-1); color: var(--text-3); font: 400 var(--t-micro)/1.4 var(--font-ui); }
.mega-sheet > a:focus-visible, .mm-menu-close:focus-visible { outline: 2px solid var(--focus); outline-offset: -2px; }
@media(max-width:479px) { .mm-menu.mega-sheet { grid-template-columns: minmax(0, 1fr); } }
</style>
